import React, { useRef, useState } from "react";
import { Camera, X, ImageSquare, Spinner } from "@phosphor-icons/react";
import { isNative, nativeCameraTake, nativeCameraPickFromLibrary } from "@/lib/native";

/**
 * PhotoUploader — drag&drop foto con compressione client-side.
 *
 * - Max N foto (default 6).
 * - iter v29 r220: resize a maxWidth 2400px + JPEG quality 0.88 (HD Instagram-grade,
 *   uniformato a /lib/compressPhoto.js). Mobile web: foto a piena risoluzione fotocamera.
 * - Output: array di data URL stringhe (base64). Per ora niente upload server-side:
 *   le foto sono base64 nel JSON del POST listing (sufficiente per MVP marketplace).
 *   In futuro, sostituire con upload S3 / GridFS quando il volume cresce.
 *
 * Props:
 *  - value: string[] (data URLs)
 *  - onChange: (string[]) => void
 *  - max: number (default 6)
 *  - testid: stringa data-testid
 */
export default function PhotoUploader({ value = [], onChange, max = 6, testid = "photo-uploader" }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const canAdd = value.length < max;

  // iter v29 r168 — App Store: quando isNative, il click sull'area apre
  // un mini action sheet "Scatta foto / Da libreria" usando Capacitor
  // Camera invece dell'<input type=file> web. Questo soddisfa il requisito
  // Apple di "Native functionality" (rejection 4.2).
  const [showNativeMenu, setShowNativeMenu] = useState(false);

  const ingestDataUrl = async (dataUrl) => {
    if (!dataUrl) return;
    setBusy(true);
    try {
      // Riusiamo lo stesso resize del web flow — passa per File via fetch
      const blob = await (await fetch(dataUrl)).blob();
      const f = new File([blob], `photo-${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
      const out = await resizeImage(f, 2400, 0.88);
      onChange([...value, out]);
    } catch (e) {
      console.warn("native ingest failed", e); // eslint-disable-line no-console
    } finally {
      setBusy(false);
    }
  };

  const handleNativeShot = async () => {
    setShowNativeMenu(false);
    const photo = await nativeCameraTake({ quality: 85 });
    if (photo?.dataUrl) await ingestDataUrl(photo.dataUrl);
  };
  const handleNativeLibrary = async () => {
    setShowNativeMenu(false);
    const photo = await nativeCameraPickFromLibrary({ quality: 85, multiple: false });
    if (photo?.dataUrl) await ingestDataUrl(photo.dataUrl);
  };

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    const remaining = max - value.length;
    const files = Array.from(fileList).slice(0, remaining).filter((f) => /^image\//.test(f.type));
    const out = [];
    for (const f of files) {
      try {
        const dataUrl = await resizeImage(f, 2400, 0.88);
        out.push(dataUrl);
      } catch (e) {
        console.warn("resize failed", e);  // eslint-disable-line no-console
      }
    }
    if (out.length) onChange([...value, ...out]);
    setBusy(false);
  };

  return (
    <div data-testid={testid}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          if (canAdd) handleFiles(e.dataTransfer.files);
        }}
        onClick={() => {
          if (!canAdd) return;
          if (isNative) setShowNativeMenu(true);
          else inputRef.current?.click();
        }}
        className={`relative rounded-xl border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#7C8B6E] bg-[#F1F4ED]" : "border-[#EAE7DF] bg-[#FAF9F5]"
        } ${!canAdd ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={!canAdd || busy}
          data-testid={`${testid}-input`}
        />
        {busy ? (
          <div className="flex items-center justify-center gap-2 text-[#7C8B6E]">
            <Spinner size={18} className="animate-spin"/> <span className="text-[13px]">Comprimo…</span>
          </div>
        ) : (
          <>
            <Camera size={26} weight="duotone" className="mx-auto text-[#7C8B6E] mb-1.5"/>
            <div className="text-[13px] font-semibold text-[#1C1C1A]">
              {canAdd ? "Trascina le foto qui o clicca per sceglierle" : `Hai raggiunto il massimo (${max} foto)`}
            </div>
            <div className="text-[11px] text-[#9A968B] mt-0.5">{value.length}/{max} foto · vengono compresse automaticamente</div>
          </>
        )}
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
          {value.map((src, idx) => (
            <div key={idx} className="relative aspect-square bg-[#F1F4ED] rounded-lg overflow-hidden group" data-testid={`${testid}-thumb-${idx}`}>
              <img src={src} alt="" className="w-full h-full object-cover"/>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(value.filter((_, i) => i !== idx)); }}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#1C1C1A]/85 text-white flex items-center justify-center hover:bg-black transition-colors"
                aria-label="Rimuovi"
                data-testid={`${testid}-remove-${idx}`}
              >
                <X size={12} weight="bold"/>
              </button>
              {idx === 0 && (
                <div className="absolute bottom-1 left-1 inline-flex items-center gap-1 text-[8.5px] uppercase tracking-widest bg-white/95 text-[#5A6A4D] px-1.5 py-0.5 rounded font-bold">
                  <ImageSquare size={9} weight="fill"/> Copertina
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* iter v29 r168 — Native action sheet iOS · "Scatta / Libreria" */}
      {showNativeMenu && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center"
          onClick={() => setShowNativeMenu(false)}
          data-testid={`${testid}-native-sheet`}
        >
          <div
            className="bg-white w-full sm:max-w-xs rounded-t-2xl sm:rounded-2xl p-4 sm:m-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[13.5px] font-semibold text-[#1C1C1A] mb-3">Aggiungi foto</div>
            <button
              type="button"
              onClick={handleNativeShot}
              className="w-full text-left px-4 py-3 rounded-md hover:bg-[#F1F4ED] flex items-center gap-3 text-[14px]"
              data-testid={`${testid}-native-take`}
            >
              <Camera size={20} weight="duotone" className="text-[#7C8B6E]"/> Scatta foto
            </button>
            <button
              type="button"
              onClick={handleNativeLibrary}
              className="w-full text-left px-4 py-3 rounded-md hover:bg-[#F1F4ED] flex items-center gap-3 text-[14px]"
              data-testid={`${testid}-native-library`}
            >
              <ImageSquare size={20} weight="duotone" className="text-[#7C8B6E]"/> Scegli da libreria
            </button>
            <button
              type="button"
              onClick={() => setShowNativeMenu(false)}
              className="w-full text-center px-4 py-3 mt-1 rounded-md text-[#6B6A65] text-[13.5px] hover:bg-[#FAFAF7]"
              data-testid={`${testid}-native-cancel`}
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Resize an image File to a max width using canvas, output JPEG dataURL.
 */
function resizeImage(file, maxW = 2400, quality = 0.88) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, maxW / img.width);
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
